import { combineReducers } from "redux";
import { handleActions } from "redux-actions";
import { RSAA } from "redux-api-middleware";
import { FETCH_PATIENTS_REQUEST } from "../create-order";
import { showConfirmDialog } from "../confirm-dialog";
import { PMR_INTERFACE_API_ENDPOINT } from "redux/navigation";

const USERS_API = process.env.REACT_APP_PHARMACY_USERS_API;

// ACTIONS
export const SELECT_USER_TO_MATCH = "MATCH_USERS/SELECT_USER_TO_MATCH";

export const FETCH_AUTOCOMPLETE_USER_REQUEST =
  "MATCH_USERS/FETCH_AUTOCOMPLETE_USER_REQUEST";
export const FETCH_AUTOCOMPLETE_USER_RECEIVE =
  "MATCH_USERS/FETCH_AUTOCOMPLETE_USER_RECEIVE";
export const FETCH_AUTOCOMPLETE_USER_FAILURE =
  "MATCH_USERS/FETCH_AUTOCOMPLETE_USER_FAILURE";

export const MATCH_USER_REQUEST = "MATCH_USERS/MATCH_USER_REQUEST";
export const MATCH_USER_RECEIVE = "MATCH_USERS/MATCH_USER_RECEIVE";
export const MATCH_USER_FAILURE = "MATCH_USERS/MATCH_USER_FAILURE";

export const GET_UNMATCHED_SERVICE_REQUEST =
  "MATCH_USERS/GET_UNMATCHED_SERVICE_REQUEST";
export const GET_UNMATCHED_SERVICE_RECEIVE =
  "MATCH_USERS/GET_UNMATCHED_SERVICE_RECEIVE";
export const GET_UNMATCHED_SERVICE_FAILURE =
  "MATCH_USERS/GET_UNMATCHED_SERVICE_FAILURE";

export const SELECT_MATCHED_USER = "MATCH_USERS/SELECT_MATCHED_USER";

export const CLEAR_USER_SUGGESTIONS = "MATCH_USERS/CLEAR_USER_SUGGESTIONS";
export const CLEAR_SELECTED_USER = "MATCH_USERS/CLEAR_SELECTED_USER";
export const CLEAR_MATCHED_USER = "MATCH_USERS/CLEAR_MATCHED_USER";

// ACTION CREATORS
export const selectUser = user => ({
  type: SELECT_USER_TO_MATCH,
  payload: { user }
});
export const clearSelectedUser = () => ({
  type: CLEAR_SELECTED_USER
});

export const confirmServiceMatch = (pmrUser, pharmacyUser, selectedService) => {
  if (selectedService === "Care-Home")
    return showConfirmDialog({
      title: "Are you sure?",
      message:
        "Prescriptions for patients assigned to the wrong service may be delayed or may not reach the patient at all. Please ensure you are assigning this patient correctly. This action cannot be undone.",
      positiveActionTitle: "ASSIGN PATIENT TO CARE HOMES",
      agreeActionCreator: () =>
        matchUser(pmrUser, pharmacyUser?.userId ?? undefined, selectedService)
    });
  return showConfirmDialog({
    title: "Confirm user match",
    message:
      `This will match PMR patient ${pmrUser.firstName} ${pmrUser.lastName} with PharmOs user ` +
      `${pharmacyUser.firstName} ${pharmacyUser.lastName}.` +
      " This action cannot be undone.",
    positiveActionTitle: "Confirm",
    agreeActionCreator: () =>
      matchUser(pmrUser, pharmacyUser?.userId, selectedService)
  });
};

export const matchUser = (
  pmrUser,
  userId,
  service,
  uniqueId = Date.now(),
  apiEndpoint = PMR_INTERFACE_API_ENDPOINT
) => {
  return {
    [RSAA]: {
      endpoint: `${apiEndpoint}/unmatched/patients/${pmrUser.nhsNumber}/resolve`,
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        userId,
        service
      }),
      types: [
        MATCH_USER_REQUEST,
        MATCH_USER_RECEIVE,
        MATCH_USER_FAILURE
      ].map(type => ({ type, meta: { uniqueId } })),
      options: { addAuth: true }
    }
  };
};

export const getUnmatchedUsers = (
  page = 1,
  uniqueId = Date.now(),
  apiEndpoint = PMR_INTERFACE_API_ENDPOINT
) => {
  return {
    [RSAA]: {
      endpoint: `${apiEndpoint}/unmatched/patients?page=${page}`,
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      types: [
        GET_UNMATCHED_SERVICE_REQUEST,
        GET_UNMATCHED_SERVICE_RECEIVE,
        GET_UNMATCHED_SERVICE_FAILURE
      ].map(type => ({ type, meta: { uniqueId } })),
      options: { addAuth: true }
    }
  };
};

export const changeUserSearch = (
  text,
  uniqueId = Date.now(),
  apiEndpoint = USERS_API
) => {
  if (text.length < 2)
    return { type: FETCH_PATIENTS_REQUEST, meta: { uniqueId, text } };
  return {
    [RSAA]: {
      endpoint: `${apiEndpoint}/users/search`,
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ name: text }),
      types: [
        FETCH_AUTOCOMPLETE_USER_REQUEST,
        FETCH_AUTOCOMPLETE_USER_RECEIVE,
        FETCH_AUTOCOMPLETE_USER_FAILURE
      ].map(type => ({ type, meta: { uniqueId, text } })),
      options: { addAuth: true }
    }
  };
};

export const selectUserSuggestion = user => ({
  type: SELECT_MATCHED_USER,
  payload: { user }
});

export const clearUserSuggestions = () => ({
  type: CLEAR_USER_SUGGESTIONS
});

export const clearMatchedUser = () => ({
  type: CLEAR_MATCHED_USER
});

// SELECTORS
export const getUnmatchedServiceData = state => state.unmatchedServiceData;
export const getSelectedUser = state => state.selectedUser;
export const getSelectedMatchedUser = state => state.selectedMatchUser;
export const getUserSuggestions = state => state.userAutocomplete.suggestions;
export const getUserSearchText = state => state.userSearchText;
export const getNeedsUpdate = state => state.needsRefresh;
export const getFetching = state => state.fetching;
export const getMatching = state => state.matching;

const unmatchedServiceData = handleActions(
  {
    [GET_UNMATCHED_SERVICE_RECEIVE]: (state, action) => ({
      users:
        action.payload.data.page > state.page
          ? state.users.concat(action.payload.data.users ?? [])
          : action.payload.data.users,
      page: action.payload.data.page,
      totalPages: action.payload.data.totalPages
    })
  },
  { users: [], page: 0, totalPages: 0 }
);

const selectedUser = handleActions(
  {
    [SELECT_USER_TO_MATCH]: (state, action) => action.payload.user,
    [MATCH_USER_RECEIVE]: () => null,
    [CLEAR_SELECTED_USER]: () => null
  },
  null
);

const selectedMatchUser = handleActions(
  {
    [SELECT_MATCHED_USER]: (state, action) => action.payload.user,
    [CLEAR_MATCHED_USER]: () => null,
    [MATCH_USER_RECEIVE]: () => null
  },
  null
);

const userSearchText = handleActions(
  {
    [CLEAR_MATCHED_USER]: () => "",
    [MATCH_USER_RECEIVE]: () => "",
    [FETCH_AUTOCOMPLETE_USER_REQUEST]: (state, action) => action.meta.text || ""
  },
  ""
);

const userAutocomplete = handleActions(
  {
    [SELECT_MATCHED_USER]: () => ({ reqId: null, suggestions: [] }),
    [CLEAR_USER_SUGGESTIONS]: () => ({ reqId: null, suggestions: [] }),
    [MATCH_USER_RECEIVE]: () => ({ reqId: null, suggestions: [] }),
    [FETCH_AUTOCOMPLETE_USER_REQUEST]: (state, action) => ({
      ...state,
      reqId: action.meta.uniqueId
    }),
    [FETCH_AUTOCOMPLETE_USER_RECEIVE]: (state, action) => {
      if (action.meta.uniqueId === state.reqId) {
        return {
          suggestions: action.payload.map(p => ({
            ...p,
            address: `${p.addressLine1}, ${p.postcode}`
          })),
          reqId: null
        };
      }
      return state;
    },
    [FETCH_AUTOCOMPLETE_USER_FAILURE]: (state, action) =>
      action.meta.uniqueId === state.reqId ? { ...state, reqId: null } : state
  },
  { reqId: null, suggestions: [] }
);

const needsRefresh = handleActions(
  {
    [MATCH_USER_RECEIVE]: () => true,
    [GET_UNMATCHED_SERVICE_RECEIVE]: () => false
  },
  false
);

const fetching = handleActions(
  {
    [GET_UNMATCHED_SERVICE_REQUEST]: () => true,
    [GET_UNMATCHED_SERVICE_RECEIVE]: () => false,
    [GET_UNMATCHED_SERVICE_FAILURE]: () => false
  },
  false
);

const matching = handleActions(
  {
    [MATCH_USER_REQUEST]: () => true,
    [MATCH_USER_RECEIVE]: () => false,
    [MATCH_USER_FAILURE]: () => false
  },
  false
);

export default combineReducers({
  unmatchedServiceData,
  selectedUser,
  selectedMatchUser,
  userSearchText,
  userAutocomplete,
  needsRefresh,
  fetching,
  matching
});
